// @flow
import React, { createContext, useContext, useReducer } from 'react';
import { type CriteriaContextValuesType } from 'Components/widgets/criteria/CriteriaContext';

export const ACTIONS = Object.freeze({
  CLOSE_CRITERIA: 'CLOSE_CRITERIA',
  OPEN_CRITERIA: 'OPEN_CRITERIA',
  OPEN_NEXT_CRITERIA: 'OPEN_NEXT_CRITERIA',
});

type Criteria = 'criteriaWhen' | 'criteriaWho' | 'criteriaTransport';

export type State = {
  criteriaWhenIsOpen: boolean,
  criteriaWhoIsOpen: boolean,
  criteriaTransportIsOpen: boolean,
};

type OpenCriteriaAction = {
  type: 'OPEN_CRITERIA',
  payload: Criteria,
};
type CloseCriteriaAction = {
  type: 'CLOSE_CRITERIA',
  payload: Criteria,
};
type OpenNextCriteriaAction = {
  type: 'OPEN_NEXT_CRITERIA',
  payload: {
    currentCriteria: Criteria,
    criteriaContext: CriteriaContextValuesType,
  },
};

type Action = CloseCriteriaAction | OpenCriteriaAction | OpenNextCriteriaAction;

const initialState = {
  criteriaWhenIsOpen: false,
  criteriaWhoIsOpen: false,
  criteriaTransportIsOpen: false,
};

const criteriaContextReducer = (state: State, action: Action) => {
  switch (action.type) {
    case ACTIONS.OPEN_CRITERIA:
      return { ...state, [`${action.payload}IsOpen`]: true };

    case ACTIONS.CLOSE_CRITERIA:
      return { ...state, [`${action.payload}IsOpen`]: false };

    case ACTIONS.OPEN_NEXT_CRITERIA:
      const { currentCriteria, criteriaContext } = action.payload;

      if (currentCriteria === 'criteriaTransport') {
        return state;
      }

      if (currentCriteria === 'criteriaWho' && !criteriaContext.criteriaTransportIsSet) {
        return { ...state, criteriaTransportIsOpen: true };
      }

      if (currentCriteria === 'criteriaWhen') {
        if (!criteriaContext.criteriaWhoIsSet) {
          return { ...state, criteriaWhoIsOpen: true };
        }
        if (!criteriaContext.criteriaTransportIsSet) {
          return { ...state, criteriaTransportIsOpen: true };
        }
      }

      return state;
    default:
      return state;
  }
};

type CriteriaDisplayContextType = [State, (Action) => void];

export const CriteriaDisplayContext = createContext<CriteriaDisplayContextType>([
  initialState,
  () => {},
]);

export const useCriteriaDisplayContext = () => useContext(CriteriaDisplayContext);

export const CriteriaDisplayProvider = ({
  children,
  value = {},
}: {
  children: Node,
  value?: $Shape<State>,
}) => {
  const [state, dispatch] = useReducer(criteriaContextReducer, { ...initialState, ...value });

  return (
    <CriteriaDisplayContext.Provider value={[state, dispatch]}>
      {children}
    </CriteriaDisplayContext.Provider>
  );
};
